import React from 'react';

import { ActionIcon, Collapse } from '@cowbell/ui';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { PANEL_PADDING_REM } from './org-form-layout';
import classes from './OrgPanel.module.css';

interface OrgPanelProps {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly status?: React.ReactNode;
  readonly children: React.ReactNode;
}

/**
 * One card of the organization profile: a heading, a completion status, and a
 * collapsible body of fields.
 *
 * <p>Renders a real `<section id>` because both the section rail and the page's
 * IntersectionObserver address panels by id.
 */
export const OrgPanel = ({
  id,
  title,
  description,
  status,
  children,
}: OrgPanelProps) => {
  const [open, setOpen] = React.useState(true);

  const toggleLabel = open ? `Collapse ${title}` : `Expand ${title}`;

  return (
    <section id={id} aria-labelledby={`${id}-title`} className={classes.panel}>
      <div className={classes.header} style={{ padding: PANEL_PADDING_REM }}>
        <div>
          <h2 id={`${id}-title`} className={classes.title}>
            {title}
          </h2>
          <p className={classes.description}>{description}</p>
        </div>
        <div className={classes.status}>
          {status}
          <ActionIcon
            variant="subtle"
            color="gray"
            aria-expanded={open}
            aria-controls={`${id}-content`}
            aria-label={toggleLabel}
            onClick={() => setOpen((previous) => !previous)}
          >
            <ExpandMoreIcon
              fontSize="small"
              className={open ? classes.chevronOpen : undefined}
            />
          </ActionIcon>
        </div>
      </div>
      <Collapse in={open} id={`${id}-content`}>
        <div style={{ padding: PANEL_PADDING_REM }}>{children}</div>
      </Collapse>
    </section>
  );
};

export default OrgPanel;
